<template>
  <div class="flex min-h-screen flex-col bg-bg">
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-1100 focus:px-3 focus:py-1.5 focus:rounded-md focus:bg-accent focus:text-text-inverse focus:no-underline focus:outline-2 focus:outline-accent-hover focus:outline-offset-2"
    >{{ messages.page.skipToMain }}</a>
    <AppHeader />
    <AppNav />
    <main id="main-content" aria-labelledby="main-heading" class="flex w-full flex-1 flex-col p-4 sm:p-6" tabindex="-1">
      <h1 id="main-heading" class="sr-only">{{ heading }}</h1>
      <RaidRecoveryBanner />
      <div id="calendar-content" tabindex="-1">
        <RouterView />
      </div>
    </main>
    <StatusBar :hints="statusHints" />
  </div>
</template>

<script setup lang="ts">
/**
 * App shell. The host chrome (`AppHeader`, `AppNav`, `StatusBar`) sits
 * around a `<RouterView>`. Phase 8 keeps this file feature-agnostic:
 * the only feature-local import is `<RaidRecoveryBanner />`, a component
 * that reads its own store internally — that's a feature reading its
 * own store, allowed by the architecture.
 *
 * Locale reactivity:
 *   - The `<html lang>` attribute mirrors `localePort.locale` so screen
 *     readers and browser translation surfaces pick up the change.
 *   - The visually-hidden `<h1>` heading and the keyboard hints in
 *     the status footer both resolve through `useLocaleMessages(...)`
 *     so a locale flip repaints them on the next render.
 */
import { computed, watchEffect } from 'vue'
import { useRoute } from 'vue-router'

import AppHeader from '@/app/layout/AppHeader.vue'
import AppNav from '@/app/layout/AppNav.vue'
import StatusBar from '@/app/layout/StatusBar.vue'
import type { StatusBarHint } from '@/app/layout/StatusBar.vue'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { FEATURE_ROUTE_NAMES } from '@/features/gvg-planner/constants/routes'
import RaidRecoveryBanner from '@/app/layout/RaidRecoveryBanner.vue'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { localePort } from '@/app/locale'
import { appShellEn } from '@/app/layout/locales/en.ts'
import { appShellRu } from '@/app/layout/locales/ru.ts'

const route = useRoute()
const catalog = defineLocaleCatalog({ en: appShellEn, ru: appShellRu })
const messages = useLocaleMessages(catalog)

/**
 * Mirror the effective locale onto `<html lang>` so assistive tech and
 * browser translation pick up the active language. The watch is
 * immediate (default) — the very first effect run syncs the attribute
 * for the initial paint, and every subsequent locale flip repaints it.
 */
watchEffect(() => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = localePort.locale.value
  }
})

const heading = computed<string>(() => {
  switch (route.name) {
    case FEATURE_ROUTE_NAMES.GvgPlanner: return messages.value.heading.planner
    case APP_ROUTE_NAMES.Calendar: return messages.value.heading.calendar
    case FEATURE_ROUTE_NAMES.Officers: return messages.value.heading.officers
    case FEATURE_ROUTE_NAMES.Onboarding: return messages.value.heading.onboarding
    default: return messages.value.heading.default
  }
})

const statusHints = computed<ReadonlyArray<StatusBarHint>>(() => {
  if (route.name === APP_ROUTE_NAMES.Calendar) {
    return [
      { id: 'h-arrow', key: '←/→', label: messages.value.statusHints.calendar.day },
      { id: 'h-week', key: 'PgUp/Dn', label: messages.value.statusHints.calendar.week },
      { id: 'h-today', key: 'Today', label: messages.value.statusHints.calendar.currentWeek },
      { id: 'h-escape', key: 'Esc', label: messages.value.statusHints.calendar.closeCalendar }
    ]
  }
  if (route.name === FEATURE_ROUTE_NAMES.GvgPlanner) {
    return [
      { id: 'h-drag', key: 'Drag', label: messages.value.statusHints.planner.drag },
      { id: 'h-click', key: 'Click', label: messages.value.statusHints.planner.click },
      { id: 'h-n', key: 'N', label: messages.value.statusHints.planner.newPlayer },
    ]
  }
  return []
})
</script>